/**
 * Motore di calcolo del bilancio Camposcuola (v3).
 *
 * Modello: ereditarietà (notti) + override (quote) + voci libere di costo/entrata.
 *
 * Regole chiave (concordate col business plan Juniores-Suneimi):
 * - La QUOTA BASE si calcola sulle NOTTI PIENE di tutti i partecipanti attivi:
 *   (spese teoriche a notti piene − entrate fisse) / n° a quota standard.
 *   Così la riduzione di notti di un singolo non cambia la quota degli altri.
 * - Chi è a quota base (animatori e ospiti) e fa meno notti paga:
 *   quota − (notti mancanti × costo notte). GAP-NEUTRALE: il minor incasso è
 *   esattamente compensato dal minor costo struttura.
 * - Il margine sopra il costo struttura pieno (es. 190 − 171 = 19€) è uguale per
 *   tutti i partecipanti a quota base ed è esposto come voce d'entrata separata:
 *   "Maggiorazione quota" (maggiorazione_quota).
 * - Gli sconti (override individuale a parità di notti) restano a carico del GAP.
 * - Responsabile con meno notti: la sua quota fissa non cambia, il GAP scende.
 * - 0 notti = escluso: fuori da costi e quote, paga 0.
 * - GAP = spese totali − (Σ dovuti + Σ voci d'entrata) = quota parrocchia (massimo).
 *
 * Tutte le funzioni sono pure: stesso input → stesso output, nessun side effect.
 */

// ---------------------------------------------------------------------------
// Tipi
// ---------------------------------------------------------------------------

/** @typedef {"animatore" | "responsabile" | "ospite"} RuoloCamposcuola */

/**
 * @typedef {Object} VoceImporto
 * @property {string} [id]
 * @property {string} descrizione  Es. "Merende", "Autofinanziamento torneo"
 * @property {number} importo
 */

/**
 * @typedef {Object} ImpostazioniEvento
 * @property {number} costo_notte_struttura      Costo per persona/notte (es. 57)
 * @property {number} notti_totali_evento        Notti dell'evento (es. 3)
 * @property {number | null} [override_globale_quota_base]  Quota tonda che sostituisce la matematica (es. 190)
 * @property {VoceImporto[]} [voci_spese_extra]  Voci libere di costo (es. merende, materiale)
 * @property {VoceImporto[]} [voci_entrate]      Voci libere d'entrata (es. autofinanziamenti)
 * @property {number} [budget_spese_extra]       LEGACY (v2, scalare): usato solo se voci_spese_extra è assente
 * @property {number} [entrate_autofinanziamento] LEGACY (v2, scalare): usato solo se voci_entrate è assente
 */

/**
 * @typedef {Object} Partecipante
 * @property {string} id
 * @property {string} [nome]
 * @property {string} [cognome]
 * @property {RuoloCamposcuola} ruolo
 * @property {number | null} [notti_effettive]  null/undefined → eredita notti_totali_evento
 * @property {number | null} [override_quota]   Se valorizzato (anche 0), il dovuto si blocca qui
 * @property {string} [motivo_override]
 * @property {number} importo_versato
 */

/**
 * @typedef {Object} RigaBilancio
 * @property {Partecipante} partecipante
 * @property {number} notti                Notti effettive risolte
 * @property {number} costo_struttura      Costo che genera per la struttura (notti × costo notte)
 * @property {number} dovuto
 * @property {number} quota_struttura      Parte del dovuto che copre la sua struttura (righe a quota base)
 * @property {number} quota_margine        Parte del dovuto sopra la struttura ("maggiorazione", righe a quota base)
 * @property {"quota_base" | "override" | "escluso" | "non_pagante"} tipo_quota
 * @property {number} residuo              max(0, dovuto - versato)
 */

/**
 * @typedef {Object} Bilancio
 * @property {number} spese_struttura           Struttura a notti EFFETTIVE (costo reale)
 * @property {number} spese_struttura_teoriche  Struttura a notti PIENE (base del calcolo quota)
 * @property {number} totale_voci_spese         Σ voci libere di costo
 * @property {number} spese_totali              Step 1: struttura effettiva + voci di costo
 * @property {number} totale_override           Σ override_quota dei partecipanti attivi
 * @property {number} totale_voci_entrate       Σ voci libere d'entrata
 * @property {number} entrate_fisse             Step 2: voci d'entrata + override
 * @property {number} n_quota_standard          Animatori e ospiti che pagano la quota base
 * @property {number | null} quota_matematica   Step 3 (su notti piene), prima dell'arrotondamento
 * @property {number | null} quota_applicata    Quota effettivamente applicata
 * @property {"matematica" | "override_globale" | null} fonte_quota
 * @property {number} maggiorazione_quota       Σ margini sopra la struttura (voce d'entrata separata)
 * @property {number} entrate_simulate          Σ dovuti + voci d'entrata (se tutti saldano)
 * @property {number} gap                       Step 4: spese_totali − entrate_simulate (= quota parrocchia max)
 * @property {number} totale_versato
 * @property {number} residuo_da_incassare
 * @property {number} scoperto_attuale          spese_totali − versato − voci d'entrata (fotografia ad oggi)
 * @property {RigaBilancio[]} righe
 * @property {string[]} anomalie
 */

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/** Arrotonda ai centesimi (evita gli artefatti float di JS) */
export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const num = (v, fallback = 0) => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

/** Somma di una lista di voci {descrizione, importo} */
export const totaleVoci = (voci) => round2((voci || []).reduce((acc, v) => acc + num(v.importo), 0));

/** Voci di costo, con fallback sullo scalare legacy v2 */
export const vociSpese = (imp) =>
  Array.isArray(imp.voci_spese_extra) && imp.voci_spese_extra.length > 0
    ? imp.voci_spese_extra
    : num(imp.budget_spese_extra) > 0
      ? [{ id: "legacy-spese", descrizione: "Spese extra", importo: num(imp.budget_spese_extra) }]
      : [];

/** Voci d'entrata, con fallback sullo scalare legacy v2 */
export const vociEntrate = (imp) =>
  Array.isArray(imp.voci_entrate) && imp.voci_entrate.length > 0
    ? imp.voci_entrate
    : num(imp.entrate_autofinanziamento) > 0
      ? [{ id: "legacy-autof", descrizione: "Autofinanziamento", importo: num(imp.entrate_autofinanziamento) }]
      : [];

/** true se il partecipante ha una quota bloccata (0 compreso) */
export const hasOverride = (p) => p.override_quota !== null && p.override_quota !== undefined;

/** Notti effettive risolte: override individuale se presente, altrimenti eredita */
export const nottiEffettive = (p, impostazioni) => {
  const notti = p.notti_effettive ?? impostazioni.notti_totali_evento;
  return Math.max(0, Math.min(num(notti), num(impostazioni.notti_totali_evento)));
};

/** Partecipa all'evento? (0 notti = defezione totale: fuori da costi e quote) */
export const partecipa = (p, impostazioni) => nottiEffettive(p, impostazioni) > 0;

// ---------------------------------------------------------------------------
// Step 1 — Spese
// ---------------------------------------------------------------------------

/**
 * Struttura a notti effettive (costo reale) + struttura a notti piene (base quota) + voci di costo.
 * @param {Partecipante[]} partecipanti
 * @param {ImpostazioniEvento} impostazioni
 */
export function calcSpeseTotali(partecipanti, impostazioni) {
  const costoNotte = num(impostazioni.costo_notte_struttura);
  const attivi = partecipanti.filter((p) => partecipa(p, impostazioni));
  const nottiEff = partecipanti.reduce((acc, p) => acc + nottiEffettive(p, impostazioni), 0);
  const spese_struttura = round2(nottiEff * costoNotte);
  const spese_struttura_teoriche = round2(attivi.length * num(impostazioni.notti_totali_evento) * costoNotte);
  const totale_voci_spese = totaleVoci(vociSpese(impostazioni));
  return {
    spese_struttura,
    spese_struttura_teoriche,
    totale_voci_spese,
    spese_totali: round2(spese_struttura + totale_voci_spese),
  };
}

// ---------------------------------------------------------------------------
// Step 2 — Entrate fisse
// ---------------------------------------------------------------------------

/**
 * Voci d'entrata + somma degli override_quota dei partecipanti attivi.
 * @param {Partecipante[]} partecipanti
 * @param {ImpostazioniEvento} impostazioni
 */
export function calcEntrateFisse(partecipanti, impostazioni) {
  const totale_override = round2(
    partecipanti
      .filter((p) => partecipa(p, impostazioni) && hasOverride(p))
      .reduce((acc, p) => acc + num(p.override_quota), 0)
  );
  const totale_voci_entrate = totaleVoci(vociEntrate(impostazioni));
  return {
    totale_override,
    totale_voci_entrate,
    entrate_fisse: round2(totale_override + totale_voci_entrate),
  };
}

// ---------------------------------------------------------------------------
// Step 3 — Quota base (animatori e ospiti), calcolata su notti piene
// ---------------------------------------------------------------------------

/** Animatori e ospiti attivi senza override: sono loro a dividersi il costo residuo */
export const partecipantiQuotaStandard = (partecipanti, impostazioni) =>
  partecipanti.filter(
    (p) => (p.ruolo === "animatore" || p.ruolo === "ospite") && partecipa(p, impostazioni) && !hasOverride(p)
  );

/**
 * (Spese teoriche a notti piene − Entrate fisse) / n° a quota standard.
 * NB: usa le spese TEORICHE, non quelle effettive: così la quota non cambia
 * quando un singolo riduce le notti (regola GAP-neutrale).
 * @param {Partecipante[]} partecipanti
 * @param {ImpostazioniEvento} impostazioni
 * @param {number} spese_teoriche_totali  struttura a notti piene + voci di costo
 * @param {number} entrate_fisse
 */
export function calcQuotaBase(partecipanti, impostazioni, spese_teoriche_totali, entrate_fisse) {
  const n = partecipantiQuotaStandard(partecipanti, impostazioni).length;
  const quota_matematica = n > 0 ? round2(Math.max(0, spese_teoriche_totali - entrate_fisse) / n) : null;
  const overrideGlobale = impostazioni.override_globale_quota_base;
  const haOverrideGlobale = overrideGlobale !== null && overrideGlobale !== undefined;

  return {
    n_quota_standard: n,
    quota_matematica,
    quota_applicata: haOverrideGlobale ? round2(num(overrideGlobale)) : quota_matematica,
    fonte_quota: n === 0 && !haOverrideGlobale ? null : haOverrideGlobale ? "override_globale" : "matematica",
  };
}

// ---------------------------------------------------------------------------
// Dovuto del singolo partecipante
// ---------------------------------------------------------------------------

/**
 * Quota base con notti ridotte: quota − (notti mancanti × costo notte).
 * Il margine sopra la struttura resta identico per tutti (→ maggiorazione).
 * @param {Partecipante} p
 * @param {ImpostazioniEvento} impostazioni
 * @param {number|null} quota_applicata
 */
export function dovutoPartecipante(p, impostazioni, quota_applicata) {
  if (!partecipa(p, impostazioni)) return { dovuto: 0, tipo_quota: "escluso" };
  if (hasOverride(p)) return { dovuto: round2(num(p.override_quota)), tipo_quota: "override" };
  if ((p.ruolo === "animatore" || p.ruolo === "ospite") && quota_applicata !== null) {
    const mancanti = num(impostazioni.notti_totali_evento) - nottiEffettive(p, impostazioni);
    const dovuto = round2(Math.max(0, quota_applicata - mancanti * num(impostazioni.costo_notte_struttura)));
    return { dovuto, tipo_quota: "quota_base" };
  }
  // Responsabile senza override: il modello non prevede una quota per lui.
  return { dovuto: 0, tipo_quota: "non_pagante" };
}

// ---------------------------------------------------------------------------
// Step 4 + aggregato — Bilancio completo
// ---------------------------------------------------------------------------

/**
 * Esegue l'intero algoritmo. È l'unica funzione da chiamare ad ogni cambio di stato.
 * @param {Partecipante[]} partecipanti
 * @param {ImpostazioniEvento} impostazioni
 * @returns {Bilancio}
 */
export function computeBilancio(partecipanti, impostazioni) {
  const costoNotte = num(impostazioni.costo_notte_struttura);
  const { spese_struttura, spese_struttura_teoriche, totale_voci_spese, spese_totali } =
    calcSpeseTotali(partecipanti, impostazioni);
  const { totale_override, totale_voci_entrate, entrate_fisse } =
    calcEntrateFisse(partecipanti, impostazioni);
  const spese_teoriche_totali = round2(spese_struttura_teoriche + totale_voci_spese);
  const { n_quota_standard, quota_matematica, quota_applicata, fonte_quota } =
    calcQuotaBase(partecipanti, impostazioni, spese_teoriche_totali, entrate_fisse);

  const anomalie = [];
  const righe = partecipanti.map((p) => {
    const notti = nottiEffettive(p, impostazioni);
    const { dovuto, tipo_quota } = dovutoPartecipante(p, impostazioni, quota_applicata);
    if (tipo_quota === "non_pagante") {
      anomalie.push(
        `${p.nome || ""} ${p.cognome || ""} (${p.ruolo}) non ha né quota base né override: dovuto = 0. Imposta un override_quota (es. quota staff).`.trim()
      );
    }
    const costo_struttura = round2(notti * costoNotte);
    const quota_struttura = tipo_quota === "quota_base" ? costo_struttura : 0;
    const quota_margine = tipo_quota === "quota_base" ? round2(dovuto - quota_struttura) : 0;
    return {
      partecipante: p,
      notti,
      costo_struttura,
      dovuto,
      quota_struttura,
      quota_margine,
      tipo_quota,
      residuo: round2(Math.max(0, dovuto - num(p.importo_versato))),
    };
  });

  if (n_quota_standard === 0 && fonte_quota === null) {
    anomalie.push("Nessun partecipante a quota standard: l'intero costo ricade su override e voci d'entrata.");
  }

  // Voce d'entrata separata: il margine sopra la struttura chiesto a chi è a quota base
  const maggiorazione_quota = round2(righe.reduce((acc, r) => acc + r.quota_margine, 0));

  const totale_dovuto = round2(righe.reduce((acc, r) => acc + r.dovuto, 0));
  const entrate_simulate = round2(totale_dovuto + totale_voci_entrate);

  // GAP = quota parrocchia (massimo): >0 scoperto, <0 surplus
  const gap = round2(spese_totali - entrate_simulate);

  const totale_versato = round2(righe.reduce((acc, r) => acc + num(r.partecipante.importo_versato), 0));

  return {
    spese_struttura,
    spese_struttura_teoriche,
    totale_voci_spese,
    spese_totali,
    totale_override,
    totale_voci_entrate,
    entrate_fisse,
    n_quota_standard,
    quota_matematica,
    quota_applicata,
    fonte_quota,
    maggiorazione_quota,
    entrate_simulate,
    gap,
    totale_versato,
    residuo_da_incassare: round2(totale_dovuto - totale_versato),
    scoperto_attuale: round2(spese_totali - totale_versato - totale_voci_entrate),
    righe,
    anomalie,
  };
}
